<template>
  <div class="container-fluid py-5">
    <div class="row justify-content-between align-items-center mb-4">
      <div class="col">
        <h1>ESO Power BI Upload</h1>
      </div>
      <div class="col-auto d-flex gap-3 align-items-center">
        <div class="d-flex align-items-center gap-2">
          <label class="small fw-semibold mb-0" :for="monthId">Month</label>
          <MonthPickerComponent
            v-model="selectedMonthYear"
            :inputId="monthId"
            class="eso-month"
          />
        </div>
      </div>
    </div>

    <!-- Upload Card -->
    <PowerBiUploadCard @upload:success="handleUploadSuccess" />

    <!-- Status Lifecycle Info -->
    <div class="card mb-4">
      <div class="card-body">
        <h6 class="card-title mb-3">
          <font-awesome-icon icon="fas fa-info-circle" class="me-2" />
          Status Lifecycle
        </h6>
        <div class="row g-3">
          <div class="col-md-4 col-lg-2">
            <div class="d-flex align-items-start">
              <span class="badge bg-warning me-2 mt-1">Pending</span>
              <small class="text-muted">
                Data uploaded from Power BI, ready to send to Grexel
              </small>
            </div>
          </div>
          <div class="col-md-4 col-lg-2">
            <div class="d-flex align-items-start">
              <span class="badge bg-info me-2 mt-1">Submitted</span>
              <small class="text-muted">
                Sent to Grexel, awaiting confirmation
              </small>
            </div>
          </div>
          <div class="col-md-4 col-lg-2">
            <div class="d-flex align-items-start">
              <span class="badge bg-success me-2 mt-1">Accepted</span>
              <small class="text-muted">
                Confirmed by Grexel, awaiting processing
              </small>
            </div>
          </div>
          <div class="col-md-4 col-lg-3">
            <div class="d-flex align-items-start">
              <span class="badge bg-primary me-2 mt-1">Completed</span>
              <small class="text-muted"> Certificates issued by Grexel </small>
            </div>
          </div>
          <div class="col-md-4 col-lg-3">
            <div class="d-flex align-items-start">
              <span class="badge bg-danger me-2 mt-1">Rejected</span>
              <small class="text-muted">
                Rejected by Grexel, needs correction
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Grexel Actions Bar -->
    <GrexelActionsBar
      :company-records="companyRecords"
      :year="selectedYear"
      :month="selectedMonth"
      @grexel:sent="handleGrexelSent"
      @grexel:fetched="handleGrexelFetched"
    />

    <!-- Results Table -->
    <PowerBiResultsTable
      :year="selectedYear"
      :month="selectedMonth"
      :key="`${selectedYear}-${selectedMonth}-${tableRefreshKey}`"
      @update:selected-records="handleSelectedRecordsUpdate"
    />
  </div>
</template>

<script setup lang="ts">
import MonthPickerComponent from "@/components/common/MonthPickerComponent.vue";
import { ref, useId } from "vue";
import GrexelActionsBar from "./power-bi/GrexelActionsBar.vue";
import PowerBiResultsTable from "./power-bi/PowerBiResultsTable.vue";
import PowerBiUploadCard from "./power-bi/PowerBiUploadCard.vue";
import type {
  CompanyRecords,
  LtPowerBiBulkUploadResponse,
} from "./power-bi/types";
import { useEsoSelectedMonth } from "./useEsoSelectedMonth";

const { selectedMonthYear, selectedYear, selectedMonth } =
  useEsoSelectedMonth();
const monthId = useId();

// Selection state
const companyRecords = ref<CompanyRecords[]>([]);

// Table refresh key - increment to force re-fetch
const tableRefreshKey = ref(0);

const handleUploadSuccess = (_result: LtPowerBiBulkUploadResponse) => {
  // Refresh table to show newly uploaded data
  tableRefreshKey.value++;
};

const handleSelectedRecordsUpdate = (records: CompanyRecords[]) => {
  companyRecords.value = records;
};

const handleGrexelSent = () => {
  // Clear selection and refresh table after sending to Grexel
  companyRecords.value = [];
  tableRefreshKey.value++;
};

const handleGrexelFetched = () => {
  // Refresh table to show updated statuses from Grexel
  tableRefreshKey.value++;
};
</script>

<style scoped lang="scss">
.eso-month {
  width: 160px;
}

.container-fluid {
  max-width: 100%;
  padding-left: 1.5rem;
  padding-right: 1.5rem;
}

.card {
  border-radius: 1rem;
  border: 1px solid var(--phoenix-gray-200);
  width: 100%;
}

.card-header {
  background-color: transparent;
  border-bottom: 1px solid var(--phoenix-gray-200);
  padding: 1rem;
}
</style>
